import { animateItemsIn } from './modules/animate-items-in.js';
import { getResponsiveDevice } from './modules/viewport-device.js';

/**
 * "Load More" AJAX for the Event Feed block (jctc/event-feed,
 * blocks/event-feed), which lists jctc_event_page posts from the shared
 * Personal Pages feature area (inc/event-personal-pages.php):
 *
 * - Event Feed (blocks/event-feed): .js-personal-pages-list/
 *   .js-load-personal-pages, action load_more_event_feed, forwards the
 *   current Category/Country filter (read from the URL query string) so a
 *   filtered view stays filtered across "Load More" pages too.
 *
 * Deliberately uses its OWN grid/button classes, not .js-events-grid/
 * .js-load-events -- reusing those would make events-ajax.js's own
 * MutationObserver bind to this grid too and fire load_more_events (live
 * seetickets-event posts) instead of the dedicated action above. Modeled
 * on blog-ajax.js's simpler guard-clause shape, not events-ajax.js's
 * block-editor-preview/responsive-row complexity -- this page never
 * renders inside the block editor canvas.
 *
 * Per-viewport Initial Count / Load More Count (block sidebar, Desktop/
 * Tablet/Mobile tabs -- acf-json/group_jctc_event_feed_block.json,
 * read server-side by blocks/event-feed/render.php into
 * data-initial-{device} on .js-personal-pages-list and
 * data-load-count-{device} on .js-load-personal-pages): this list has no
 * column grid of its own (.personal-pages-list is a plain flex column,
 * unlike .events-grid), so "per viewport" here means a flat row count, not
 * events-ajax.js's columns x rows. adjustInitialView() below and the click
 * handler's own "reveal hidden rows first, fetch only once none are left"
 * two-phase shape otherwise mirror events-ajax.js's adjustInitialView()/
 * click handler directly -- see that file's own doc comments for the
 * revealedCount "floor" rationale (a resize firing right after a reveal,
 * e.g. a mobile browser's URL bar auto-hiding while the page scrolls, must
 * never re-collapse rows the visitor already expanded).
 */

const deviceDataKey = (prefix, device) => `${prefix}${device[0].toUpperCase()}${device.slice(1)}`;

const initLoadMore = ({
    gridSelector,
    buttonSelector,
    itemSelector,
    wrapperSelector,
    action,
    getExtraParams,
}) => {
    const grid = document.querySelector(gridSelector);
    const loadBtn = document.querySelector(buttonSelector);
    if (!grid || !loadBtn) return;

    const setLoadButtonVisible = (isVisible) => {
        const wrapper = loadBtn.closest(wrapperSelector);
        loadBtn.hidden = !isVisible;
        if (wrapper) wrapper.hidden = !isVisible;
    };

    const getTotalEvents = () => parseInt(loadBtn.dataset.total || '0', 10) || 0;
    const getRenderedCount = () => grid.querySelectorAll(itemSelector).length;

    const FALLBACK_COUNT = { desktop: 8, tablet: 8, mobile: 8 };
    const getInitialCount = () => {
        const device = getResponsiveDevice();
        const fromData = parseInt(grid.dataset[deviceDataKey('initial', device)] || '', 10);
        return Number.isFinite(fromData) && fromData > 0 ? fromData : FALLBACK_COUNT[device];
    };
    const getLoadCount = () => {
        const device = getResponsiveDevice();
        const fromData = parseInt(loadBtn.dataset[deviceDataKey('loadCount', device)] || '', 10);
        return Number.isFinite(fromData) && fromData > 0 ? fromData : FALLBACK_COUNT[device];
    };

    // See this file's own doc comment above -- written ONLY by the click
    // handler below, never by adjustInitialView() itself.
    let revealedCount = 0;

    const adjustInitialView = () => {
        const keepCount = Math.max(getInitialCount(), revealedCount);
        const allItems = grid.querySelectorAll(itemSelector);
        allItems.forEach((item, index) => {
            item.style.display = index < keepCount ? '' : 'none';
        });

        const totalEvents = getTotalEvents();
        if (allItems.length <= keepCount && allItems.length >= totalEvents) {
            setLoadButtonVisible(false);
            return;
        }
        setLoadButtonVisible(true);
    };

    loadBtn.addEventListener('click', async (event) => {
        event.preventDefault();

        if (loadBtn.disabled) {
            return;
        }
        loadBtn.disabled = true;

        try {
            const loadCount = getLoadCount();

            // 1. Reveal rows the server already sent (for the largest
            // viewport's Initial Count -- see blocks/event-feed/
            // render.php's own $initial_load) but hid for this viewport.
            const hiddenItems = grid.querySelectorAll(`${itemSelector}[style*="display: none"]`);
            if (hiddenItems.length > 0) {
                const revealed = [];
                hiddenItems.forEach((item, index) => {
                    if (index < loadCount) {
                        item.style.display = '';
                        revealed.push(item);
                    }
                });
                animateItemsIn(revealed);
                revealedCount += revealed.length;

                const remaining = grid.querySelectorAll(`${itemSelector}[style*="display: none"]`);
                if (remaining.length > 0) {
                    return;
                }
                if (getRenderedCount() >= getTotalEvents()) {
                    setLoadButtonVisible(false);
                }
                return;
            }

            // 2. Fetch more from the server.
            const offset = getRenderedCount();
            if (offset >= getTotalEvents()) {
                setLoadButtonVisible(false);
                return;
            }

            loadBtn.classList.add('loading');

            const formData = new FormData();
            formData.append('action', action);
            formData.append('offset', offset);
            formData.append('load_count', loadCount);
            formData.append('nonce', window.jctc_vars?.nonce || '');
            if (getExtraParams) {
                Object.entries(getExtraParams()).forEach(([key, value]) => {
                    formData.append(key, value);
                });
            }

            try {
                const response = await fetch(window.jctc_vars.ajax_url, {
                    method: 'POST',
                    body: formData,
                });
                const result = await response.json();

                if (result.success) {
                    if (result.data.html) {
                        const countBefore = getRenderedCount();
                        grid.insertAdjacentHTML('beforeend', result.data.html);
                        animateItemsIn(
                            Array.from(grid.querySelectorAll(itemSelector)).slice(countBefore)
                        );
                        revealedCount = getRenderedCount();
                    }

                    loadBtn.dataset.total =
                        result.data.total_events || loadBtn.dataset.total || '0';
                    if (getRenderedCount() >= getTotalEvents()) {
                        setLoadButtonVisible(false);
                    }
                }
            } catch (error) {
                console.error('Personal Pages AJAX Load Error:', error);
            } finally {
                loadBtn.classList.remove('loading');
            }
        } finally {
            loadBtn.disabled = false;
        }
    });

    adjustInitialView();
    let resizeTimer;
    const onResize = () => {
        if (!grid.isConnected) {
            window.removeEventListener('resize', onResize);
            return;
        }
        clearTimeout(resizeTimer);
        resizeTimer = setTimeout(adjustInitialView, 200);
    };
    window.addEventListener('resize', onResize);
};

export const initEventFeedAjax = () => {
    initLoadMore({
        gridSelector: '.js-personal-pages-list',
        buttonSelector: '.js-load-personal-pages',
        itemSelector: '.js-personal-page-row',
        wrapperSelector: '.personal-pages-load-more',
        action: 'load_more_event_feed',
        getExtraParams: () => {
            const params = new URLSearchParams(window.location.search);
            return {
                category: params.get('jctc_category') || '',
                country: params.get('jctc_country') || '',
                sort: params.get('jctc_sort') || '',
                page_url: window.location.origin + window.location.pathname,
            };
        },
    });
};
